import { Component, OnInit } from '@angular/core'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { UserRole } from '@data/enums/user-role.enum'
import { EpicWithStatus } from '@modules/private/worklog/data/interfaces/epic.interface'
import { ProjectPayload, ProjectWithStatus } from '@modules/private/worklog/data/interfaces/project.interface'
import { WorklogService, worklogErrorMessage } from '@modules/private/worklog/data/services/worklog.service'
import { EPIC_CATEGORIES } from '../data/constants/roadmap.constants'

/**
 * Página del Roadmap interno. Tres vistas: el mapa de trabajo (Backlog + Tablero), las métricas de
 * desempeño de TI y los PROYECTOS.
 *
 * La tercera era «Metas y visión» y mezclaba dos cosas distintas (ADR-0048 · D1): frentes de
 * trabajo que ya ocurrieron —cuya duración se lee en Tiempos, donde significa algo— e iniciativas
 * grandes que aún no arrancan. Ahora esta pantalla es solo lo segundo, y ahí se registran, se les
 * bajan requerimientos y se les pone precio.
 *
 * Todo sale de la base (ADR-0040): antes se cargaba `assets/data/roadmap.json`, un dataset que se
 * editaba a mano y divergía del tablero en cuanto una tarea se movía.
 */
@Component({
  selector: 'app-roadmap-page',
  templateUrl: './roadmap-page.component.html',
  styleUrls: ['./roadmap-page.component.scss']
})
export class RoadmapPageComponent implements OnInit {
  epics: EpicWithStatus[] = []
  projects: ProjectWithStatus[] = []
  loading = true
  //* Separado de `loading`: los proyectos se piden aparte y solo para Admin. Sin su propio flag, la
  //* rejilla decía «todavía no hay proyectos» mientras estaban en camino — afirmaba algo que no
  //* sabía, que es peor que no decir nada.
  loadingProjects = true

  /** Vista activa: mapa de trabajo · métricas de TI · proyectos. */
  view: 'work' | 'metrics' | 'projects' = 'work'

  /**
   * Solo Admin ve los tabs «Métricas» y «Proyectos». Los demás internos
   * (Operaciones/Finanzas/Legal/Seller) solo ven «Mapa de trabajo». El backend/route gatean por
   * `roadmap.view`; esto refina la UI. Y el precio, además, lo poda el backend.
   */
  isAdmin = false

  selectedEpic: EpicWithStatus | null = null
  selectedProject: ProjectWithStatus | null = null
  dialogVisible = false
  projectDialogVisible = false

  // ── Alta ──
  createVisible = false
  nuevoNombre = ''
  nuevaCategoria = 'modules'

  saving = false
  errorMsg: string | null = null

  //* Ref ESTABLE del config del diálogo: un objeto literal nuevo en cada ciclo hacía que PrimeNG
  //* re-creara su <style> y reposicionara el modal en cada detección de cambios.
  //* La misma lista que pinta la rejilla: dos fuentes para las mismas cinco categorías es una
  //* que se queda atrás el día que se agregue la sexta.
  readonly CATEGORIES = EPIC_CATEGORIES

  readonly dialogStyle = { width: '680px' }
  readonly dialogBreakpoints = { '740px': '94vw' }

  constructor(private worklog: WorklogService, private auth: AuthService) {}

  ngOnInit(): void {
    this.isAdmin = this.auth.getUser?.user?.permissions?.name === UserRole.ADMIN
    // Defensa: un interno no-Admin no debe quedar parado en un tab que no puede ver.
    if (!this.isAdmin) this.view = 'work'

    this.worklog.getEpics().subscribe((epics) => {
      this.epics = epics
      this.loading = false
    })
    if (this.isAdmin) this.reloadProjects()
  }

  private reloadProjects(): void {
    this.loadingProjects = true
    this.worklog.getProjects().subscribe((projects) => {
      this.projects = projects
      this.loadingProjects = false
      //* El detalle abierto se refresca contra la respuesta nueva: sin esto, guardar deja a la
      //* vista los datos de antes y parece que no pasó nada.
      if (this.selectedProject) {
        const id = this.selectedProject.project._id
        this.selectedProject = projects.find((p) => p.project._id === id) ?? null
        if (!this.selectedProject) this.projectDialogVisible = false
      }
    })
  }

  get startedCount(): number {
    return this.projects.filter((p) => p.status === 'in_progress' || p.status === 'delivered').length
  }

  // ─────────── Épicas ───────────

  onSelectEpic(e: EpicWithStatus): void {
    this.selectedEpic = e
    this.dialogVisible = true
  }

  // ─────────── Proyectos ───────────

  onSelectProject(p: ProjectWithStatus): void {
    this.selectedProject = p
    this.errorMsg = null
    this.projectDialogVisible = true
  }

  openCreate(): void {
    this.nuevoNombre = ''
    this.nuevaCategoria = 'modules'
    this.errorMsg = null
    this.createVisible = true
  }

  /**
   * Slug a partir del nombre: sin acentos, sin signos y con guiones. Es un identificador interno y
   * pedirlo a mano en el alta convertiría dos campos en tres — justo lo que hace que nadie
   * registre la idea cuando se le ocurre.
   */
  private slugify(nombre: string): string {
    return nombre
      .normalize('NFD')
      //* Clase con escapes y no con los caracteres literales: son marcas combinantes invisibles
      //* en el editor, y una copia descuidada las pierde sin que nada falle a la vista.
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
  }

  createProject(): void {
    const name = this.nuevoNombre.trim()
    if (!name || this.saving) return
    const slug = this.slugify(name)
    if (!slug) {
      this.errorMsg = 'El nombre necesita al menos una letra o número.'
      return
    }
    this.saving = true
    this.errorMsg = null
    this.worklog.createProject({ slug, name, category: this.nuevaCategoria }).subscribe({
      next: () => {
        this.saving = false
        this.createVisible = false
        this.reloadProjects()
      },
      error: (err) => {
        this.saving = false
        this.errorMsg = worklogErrorMessage(err, 'No se pudo registrar el proyecto.')
      },
    })
  }

  saveProject(dto: ProjectPayload): void {
    const p = this.selectedProject
    if (!p || this.saving) return
    this.saving = true
    this.errorMsg = null
    this.worklog.updateProject(p.project._id, dto).subscribe({
      next: () => {
        this.saving = false
        this.reloadProjects()
      },
      error: (err) => {
        this.saving = false
        this.errorMsg = worklogErrorMessage(err, 'No se pudo guardar el proyecto.')
      },
    })
  }

  removeProject(): void {
    const p = this.selectedProject
    if (!p || this.saving) return
    this.saving = true
    this.errorMsg = null
    this.worklog.removeProject(p.project._id).subscribe({
      next: () => {
        this.saving = false
        this.projectDialogVisible = false
        this.selectedProject = null
        this.reloadProjects()
      },
      error: (err) => {
        this.saving = false
        this.errorMsg = worklogErrorMessage(err, 'No se pudo retirar el proyecto.')
      },
    })
  }
}
